import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Icons } from "../../../config/MuiIcons.config";
import AppInputField from "../../../components/CommonReusableComponent/AppInputField/AppInputField";
import MUIErrorText from "../../../components/MUIErrorText/MUIErrorText";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import {
  APP_MESSAGE,
  ERROR_MESSAGE,
  SNACKBAR_VARIANT,
  USE_FORMIK_SCHEMA,
} from "../../../config/Messages.config";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import { getUserRoleAndScreens } from "../../../services/authServices/auth.services";
import {
  checkDuplicateEntity,
  replaceTextToCaplitalize,
} from "../../../common/CommonFunctions";
import {
  createAuditTypeCategory,
  updateAuditTypeCategory,
} from "../../../helpers/api-functions-helper";

const CreateOrUpdateAuditTypeCategoryModal = ({
  DialogData,
  Open,
  ToggleFunction,
  ExistingCategoryNames = [],
  FetchAllCategories = () => {},
}) => {
  const isUpdateMode = DialogData ? true : false;
  const { showSnackbar } = useSnackbar();
  const { accountId } = getUserRoleAndScreens();
  const { CREATE, UPDATE } = APP_MESSAGE.AUDIT_TYPE_CATEGORY_MASTER;
  const [isCreateOrUpdateClicked, setIsCreateOrUpdateClicked] = useState(false);

  const createOrUpdateAuditTypeCategory = async (DATA) => {
    setIsCreateOrUpdateClicked(true);
    try {
      const apiMethod = !isUpdateMode
        ? createAuditTypeCategory
        : updateAuditTypeCategory;

      const categoryRes = await apiMethod(DATA);

      const { statusCode, success, message, data } = categoryRes?.data;
      if (!success && statusCode !== 200) {
        showSnackbar(
          data || message || isUpdateMode ? UPDATE.FAILED : CREATE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      } else {
        showSnackbar(
          isUpdateMode ? UPDATE.SUCCESS : CREATE.SUCCESS,
          SNACKBAR_VARIANT.SUCCESS
        );
        handleResetDialogBox();
        FetchAllCategories();
      }
    } catch (error) {
      console.log("Error: ", error);
      showSnackbar(
        isUpdateMode ? UPDATE.FAILED : CREATE.FAILED,
        SNACKBAR_VARIANT.ERROR
      );
    } finally {
      setIsCreateOrUpdateClicked(false);
    }
  };

  // Formik form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: (DialogData && DialogData.name) || "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        // .matches(
        //   FORM_VALIDATIONS.TEXT_INPUT.REGEX.INITIAL,
        //   FORM_VALIDATIONS.TEXT_INPUT.MESAAGE.INITIAL
        // )
        .required(USE_FORMIK_SCHEMA.AUDIT_TYPE_CATEGORY_MASTER.CATEGORY_NAME),
    }),
    onSubmit: (values) => {
      const payload = {
        ...values,
      };
      if (isUpdateMode) {
        const update_payload = {
          ...payload,
          accountId: accountId,
          auditTypeCategoryId: DialogData?.id,
        };
        // console.log("update_payload: ", update_payload);
        createOrUpdateAuditTypeCategory(update_payload);
      } else {
        if (checkDuplicateEntity(ExistingCategoryNames, values.name)) {
          showSnackbar(
            APP_MESSAGE.AUDIT_TYPE_CATEGORY_MASTER.DUPLICATE_CATEGORY_NAME,
            SNACKBAR_VARIANT.ERROR
          );
        } else {
          const create_upload = {
            ...payload,
            name: replaceTextToCaplitalize(payload.name.trim()),
            accountId: accountId,
          };
          // console.log("create_payload: ", create_upload);
          createOrUpdateAuditTypeCategory(create_upload);
        }
      }
    },
  });

  const handleResetDialogBox = () => {
    ToggleFunction();
    validation.handleReset();
  };

  return (
    <React.Fragment>
      <Dialog
        open={Open}
        disableEscapeKeyDown={true}
        maxWidth="md"
        disableEnforceFocus={true}
        fullWidth
        slotProps={{
          style: {
            opacity: 0.7,
          },
        }}
      >
        <DialogTitle id="responsive-dialog-title">
          <Typography
            className="inter-medium-500"
            style={{
              fontSize: "18px",
            }}
          >
            {isUpdateMode
              ? "Update Audit Type Category"
              : "Create Audit Type Category"}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleResetDialogBox}
          style={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icons.Close />
        </IconButton>
        <DialogContent dividers>
          <div>
            <Grid2 container spacing={2} className="mb-1">
              <Grid2 size={12}>
                <Typography variant="body2" className="inter-regular-400 py-2">
                  Audit Type Category Name
                </Typography>
                <AppInputField
                  Name={"name"}
                  Id={"name"}
                  Type={"text"}
                  Disabled={isCreateOrUpdateClicked}
                  Value={validation.values.name || ""}
                  OnBlur={validation.handleBlur}
                  OnChange={validation.handleChange}
                  IsCapitalizeRequired={true}
                  // Label={"Location Name *"}
                  PlaceHolder={"Enter audit type category name"}
                  Error={
                    validation.touched.name && validation.errors.name
                      ? true
                      : false
                  }
                  HelperText={
                    validation.touched.name && validation.errors.name ? (
                      <MUIErrorText ErrorMessage={validation.errors.name} />
                    ) : null
                  }
                />
              </Grid2>
            </Grid2>
            <Grid2 container spacing={2} className="mt-3">
              {!isUpdateMode ? (
                <Grid2 size={{ xs: 6 }}>
                  <AppButton
                    Type={"reset"}
                    IsDisabled={isCreateOrUpdateClicked}
                    OnPress={validation.handleReset}
                    children={"Reset"}
                    Color={"error"}
                  />
                </Grid2>
              ) : null}
              <Grid2 size={{ xs: isUpdateMode ? 12 : 6 }}>
                <AppButton
                  Type={"submit"}
                  OnPress={() => {
                    if (navigator.onLine) {
                      validation.handleSubmit();
                    } else {
                      showSnackbar(
                        ERROR_MESSAGE.INTERNET_ERROR,
                        SNACKBAR_VARIANT.ERROR
                      );
                    }
                  }}
                  IsDisabled={isCreateOrUpdateClicked}
                  LoadingState={isCreateOrUpdateClicked}
                >
                  {DialogData !== null ? "Update" : "Create"}
                </AppButton>
              </Grid2>
            </Grid2>
          </div>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default CreateOrUpdateAuditTypeCategoryModal;
